import React, { useState } from "react";
import { Lock, Unlock, CheckCircle2, Clock, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import PhotoMedia from "@/components/PhotoMedia";
import api from "@/lib/api";
import { toast } from "sonner";
import { euro, timeAgo, initials } from "@/lib/format";

// Reusable card for a locked/unlocked photo.
const LockedPhotoCard = ({ photo, onUnlock, footer = true, allowSave = false }) => {
  const isFree = photo.is_free || photo.is_wall;
  const unlocked = isFree || photo.status === "unlocked" || photo.is_sender || photo.purchased;
  const sender = photo.sender || {};
  const [saved, setSaved] = useState(!!photo.saved_by_me);
  const [saving, setSaving] = useState(false);

  const toggleSave = async (e) => {
    e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/feed/${photo.id}/save`);
      setSaved(data.saved);
      toast.success(data.saved ? "Ajouté à vos favoris" : "Retiré des favoris");
    } catch (err) {
      toast.error("Action impossible");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid="locked-photo-card"
      className="rounded-2xl overflow-hidden border border-border bg-card shadow-[0_10px_30px_-18px_rgba(15,23,42,0.35)]"
    >
      <div className="relative aspect-[4/5]">
        <PhotoMedia photo={photo} />

        {/* Save button for public/free content */}
        {allowSave && isFree && !photo.is_sender && (
          <button
            onClick={toggleSave}
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-black/45 backdrop-blur text-white hover:bg-black/60 transition-colors"
            data-testid={`wall-save-${photo.id}`}
            aria-label="Enregistrer"
          >
            <Bookmark className={`h-4 w-4 ${saved ? "fill-white" : ""}`} />
          </button>
        )}

        {/* Lock / free badge (hidden for free public posts) */}
        {!isFree && (
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {unlocked ? (
              <Badge className="bg-[hsla(160,84%,32%,0.9)] text-white border-0 gap-1">
                <Unlock className="h-3 w-3" /> Déverrouillée
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-card/85 backdrop-blur gap-1">
                <Lock className="h-3 w-3" /> Verrouillée
              </Badge>
            )}
          </div>
        )}

        {/* Price + CTA row (only for locked private content) */}
        {!unlocked && !isFree && (
          <>
            <div
              data-testid="locked-photo-price"
              className="absolute bottom-3 left-3 rounded-full bg-card/90 backdrop-blur px-3 py-1 text-sm font-semibold"
            >
              {euro(photo.price)}
            </div>
            {onUnlock && (
              <div className="absolute bottom-3 right-3">
                <Button
                  size="sm"
                  data-testid="locked-photo-unlock-button"
                  onClick={() => onUnlock(photo)}
                  className="rounded-xl shadow-[0_10px_30px_-18px_rgba(15,23,42,0.6)]"
                >
                  <Unlock className="h-4 w-4 mr-1" /> Déverrouiller
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {footer && (
        <div className="p-3 flex items-center gap-3">
          <Avatar className="h-8 w-8">
            {sender.avatar && <AvatarImage src={sender.avatar} alt={sender.username} />}
            <AvatarFallback className="bg-accent text-accent-foreground text-[10px] font-semibold" data-testid="locked-photo-sender">
              {initials(sender.username)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">
              {photo.is_sender ? `À ${photo.receiver?.username || "…"}` : `@${sender.username}`}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {photo.caption || (unlocked ? "Photo débloquée" : "Photo privée")}
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground flex items-center gap-1 shrink-0">
            {photo.is_sender && photo.sold ? (
              <span className="text-[hsl(160_84%_32%)] flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Vendue
              </span>
            ) : photo.is_sender ? (
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> En attente</span>
            ) : (
              timeAgo(photo.created_at)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LockedPhotoCard;
